/*
  Neroli — product-gallery.js

  One scroll-snap track backs every layout, so thumbnails, arrows, the counter
  and the lightbox all read the same active index. Stacked layouts on desktop
  turn the track into a grid; mobile is always a slider.
*/

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const LARGE_SCREEN = () => window.matchMedia('(min-width: 990px)').matches;

class ProductGallery extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.track = this.querySelector('[data-gallery-track]');
    if (!this.track) return;

    this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    this.lightboxThumbs = Array.from(this.querySelectorAll('[data-lightbox-thumb]'));
    this.indexLabel = this.querySelector('[data-gallery-index]');
    this.prevButton = this.querySelector('[data-gallery-prev]');
    this.nextButton = this.querySelector('[data-gallery-next]');
    this.zoomButton = this.querySelector('[data-gallery-zoom]');
    this.lightbox = this.querySelector('[data-gallery-lightbox]');
    this.lightboxImage = this.querySelector('[data-gallery-lightbox-image]');
    this.lightboxPrev = this.querySelector('[data-lightbox-prev]');
    this.lightboxNext = this.querySelector('[data-lightbox-next]');

    this.layout = this.dataset.layout || 'slider';
    this.zoomEnabled = this.dataset.zoomEnabled === 'true';
    this.zoomMode = this.dataset.zoomMode || 'lightbox';
    this.zoomMobile = this.dataset.zoomMobile === 'true';
    this.activeIndex = 0;
    this.zoomIndex = 0;

    if (this.zoomEnabled) this.classList.add(`pgal--zoom-${this.zoomMode}`);

    this.thumbs.forEach((control) => {
      control.addEventListener('click', () => this.goToMedia(control.dataset.mediaId));
    });
    // Lightbox thumbnails drive the lightbox, not the gallery behind it.
    this.lightboxThumbs.forEach((control) => {
      control.addEventListener('click', () => this.setZoomIndex(this.indexOfMedia(control.dataset.mediaId)));
    });

    if (this.prevButton) {
      this.prevButton.hidden = false;
      this.prevButton.addEventListener('click', () => this.step(-1));
    }
    if (this.nextButton) {
      this.nextButton.hidden = false;
      this.nextButton.addEventListener('click', () => this.step(1));
    }

    // rAF-throttled: scroll fires far more often than the index can change.
    this.track.addEventListener('scroll', () => {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.syncFromScroll();
      });
    });

    this.variantListener = (event) => {
      const mediaId = event.detail && event.detail.featuredMediaId;
      if (!mediaId) return;
      const mode = this.dataset.scrollToVariant;
      if (mode === 'never') return;
      this.goToMedia(String(mediaId));
    };
    document.addEventListener('variant:change', this.variantListener);

    this.setupZoom();
    this.setActive(0);
  }

  disconnectedCallback() {
    document.removeEventListener('variant:change', this.variantListener);
  }

  /* ---- Navigation ---- */

  isSliderMode() {
    return this.layout === 'slider' || !LARGE_SCREEN();
  }

  indexOfMedia(mediaId) {
    return this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
  }

  goToMedia(mediaId) {
    const index = this.indexOfMedia(mediaId);
    if (index < 0) return;

    if (this.isSliderMode()) {
      this.track.scrollTo({
        left: index * this.track.clientWidth,
        behavior: REDUCED_MOTION ? 'auto' : 'smooth',
      });
    } else {
      // Stacked: every image is already visible, so bring it into view instead.
      this.slides[index].scrollIntoView({
        block: 'nearest',
        behavior: REDUCED_MOTION ? 'auto' : 'smooth',
      });
    }
    this.setActive(index);
  }

  step(direction) {
    const next = this.activeIndex + direction;
    if (next < 0 || next >= this.slides.length) return;
    this.goToMedia(this.slides[next].dataset.mediaId);
  }

  syncFromScroll() {
    if (!this.isSliderMode()) return;
    const width = this.track.clientWidth;
    if (!width) return;
    const index = Math.round(this.track.scrollLeft / width);
    if (index !== this.activeIndex) this.setActive(index);
  }

  setActive(index) {
    if (index < 0 || index >= this.slides.length) return;
    this.activeIndex = index;
    const mediaId = this.slides[index].dataset.mediaId;

    this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));

    this.thumbs.forEach((thumb) => {
      const active = thumb.dataset.mediaId === mediaId;
      thumb.classList.toggle('is-active', active);
      if (active) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    });

    const activeThumb = this.thumbs.find((t) => t.dataset.mediaId === mediaId);
    activeThumb?.scrollIntoView({ block: 'nearest', behavior: REDUCED_MOTION ? 'auto' : 'smooth' });

    if (this.indexLabel) this.indexLabel.textContent = String(index + 1);
    if (this.prevButton) this.prevButton.disabled = index === 0;
    if (this.nextButton) this.nextButton.disabled = index === this.slides.length - 1;
    this.updateZoomVisibility();
  }

  /* ---- Zoom ---- */

  setupZoom() {
    if (!this.zoomEnabled) return;

    if (this.zoomMode === 'hover' || this.zoomMode === 'click') {
      this.slides.forEach((slide) => {
        const image = slide.querySelector('img[data-zoom-src]');
        if (!image) return;

        const move = (event) => {
          const rect = slide.getBoundingClientRect();
          const x = ((event.clientX - rect.left) / rect.width) * 100;
          const y = ((event.clientY - rect.top) / rect.height) * 100;
          slide.style.setProperty('--zoom-x', `${x}%`);
          slide.style.setProperty('--zoom-y', `${y}%`);
        };

        const start = (event) => {
          // Magnifying is pointless on touch — there is no hover and the
          // lightbox is the better gesture, so it is desktop only.
          if (!LARGE_SCREEN()) return;
          if (image.dataset.zoomLoaded !== 'true') {
            image.src = image.dataset.zoomSrc;
            image.dataset.zoomLoaded = 'true';
          }
          slide.classList.add('is-magnifying');
          move(event);
        };
        const stop = () => slide.classList.remove('is-magnifying');

        if (this.zoomMode === 'hover') {
          slide.addEventListener('mouseenter', start);
          slide.addEventListener('mousemove', (e) => {
            if (slide.classList.contains('is-magnifying')) move(e);
          });
          slide.addEventListener('mouseleave', stop);
        } else {
          slide.addEventListener('click', (event) => {
            if (slide.classList.contains('is-magnifying')) stop();
            else start(event);
          });
          slide.addEventListener('mousemove', (e) => {
            if (slide.classList.contains('is-magnifying')) move(e);
          });
          slide.addEventListener('mouseleave', stop);
        }
      });
    }

    if (!this.lightbox || typeof this.lightbox.showModal !== 'function') return;

    this.zoomButton?.addEventListener('click', () => this.openZoom());
    this.querySelector('[data-gallery-lightbox-close]')?.addEventListener('click', () => this.lightbox.close());
    this.lightboxPrev?.addEventListener('click', () => this.stepZoom(-1));
    this.lightboxNext?.addEventListener('click', () => this.stepZoom(1));

    this.lightbox.addEventListener('click', (event) => {
      if (event.target === this.lightbox) this.lightbox.close();
    });
    this.lightbox.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.stepZoom(-1);
      if (event.key === 'ArrowRight') this.stepZoom(1);
    });

    // Leave the gallery on whatever the customer was last looking at.
    this.lightbox.addEventListener('close', () => {
      if (this.zoomIndex !== this.activeIndex) {
        this.goToMedia(this.slides[this.zoomIndex].dataset.mediaId);
      }
    });

    // Swipe, since there is no keyboard on touch and the arrows are small.
    const stage = this.querySelector('.pgal__lightbox-stage') || this.lightbox;
    let startX = null;
    stage.addEventListener('touchstart', (event) => {
      startX = event.changedTouches[0].clientX;
    }, { passive: true });
    stage.addEventListener('touchend', (event) => {
      if (startX === null) return;
      const delta = event.changedTouches[0].clientX - startX;
      startX = null;
      if (Math.abs(delta) < 40) return;
      this.stepZoom(delta < 0 ? 1 : -1);
    }, { passive: true });

    // On touch the image itself opens the lightbox, when enabled.
    if (this.zoomMobile) {
      this.slides.forEach((slide) => {
        slide.addEventListener('click', () => {
          if (LARGE_SCREEN()) return;
          if (slide.querySelector('img[data-zoom-src]')) this.openZoom();
        });
      });
    }
  }

  currentImage() {
    return this.slides[this.activeIndex]?.querySelector('img[data-zoom-src]');
  }

  updateZoomVisibility() {
    if (!this.zoomButton) return;
    const usable = this.zoomMode === 'lightbox' && typeof this.lightbox?.showModal === 'function';
    this.zoomButton.hidden = !usable || !this.currentImage();
  }

  /* The lightbox keeps its own index. It previously scrolled the gallery
     behind it and read back the result, which meant thumbnail taps and arrows
     did nothing visible while the modal was open — the element they were
     driving was inert underneath. */
  stepZoom(direction) {
    this.setZoomIndex(this.zoomIndex + direction);
  }

  setZoomIndex(index) {
    if (index < 0 || index >= this.slides.length) return;
    this.zoomIndex = index;
    this.showZoomImage();
  }

  zoomImageAt(index) {
    return this.slides[index]?.querySelector('img[data-zoom-src]');
  }

  showZoomImage() {
    const image = this.zoomImageAt(this.zoomIndex);
    if (!image) return;

    this.lightboxImage.src = image.dataset.zoomSrc;
    this.lightboxImage.alt = image.alt || '';

    const mediaId = this.slides[this.zoomIndex].dataset.mediaId;
    this.lightboxThumbs.forEach((thumb) => {
      const active = thumb.dataset.mediaId === mediaId;
      thumb.classList.toggle('is-active', active);
      if (active) {
        thumb.setAttribute('aria-current', 'true');
        thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: REDUCED_MOTION ? 'auto' : 'smooth' });
      } else {
        thumb.removeAttribute('aria-current');
      }
    });

    if (this.lightboxPrev) this.lightboxPrev.disabled = this.zoomIndex === 0;
    if (this.lightboxNext) this.lightboxNext.disabled = this.zoomIndex === this.slides.length - 1;
  }

  openZoom() {
    if (!this.currentImage()) return;
    this.zoomIndex = this.activeIndex;
    this.showZoomImage();
    this.lightbox.showModal();
  }
}

if (!customElements.get('product-gallery')) {
  customElements.define('product-gallery', ProductGallery);
}
